import { Goal, GoalProgress } from '../types/achievements';
import logger from '../utils/logger';

/**
 * Service for managing goal-related notifications
 */
class GoalNotificationsService {
  private readonly MILESTONES = [25, 50, 75, 100];

  /**
   * Check if a goal has reached a new milestone and trigger notification
   */
  checkGoalMilestone(
    goal: Goal,
    currentProgress: number,
    previousProgress: number = 0
  ): void {
    if (!goal.isActive || !goal.targetValue) return;

    // Calculate progress percentage
    const currentPercentage = Math.min(
      Math.round((currentProgress / goal.targetValue) * 100),
      100
    );
    const previousPercentage = Math.round((previousProgress / goal.targetValue) * 100);

    // Check each milestone
    for (const milestone of this.MILESTONES) {
      if (previousPercentage < milestone && currentPercentage >= milestone) {
        this.triggerMilestoneNotification(goal, milestone, currentProgress);
        break; // Only notify for the first milestone reached
      }
    }
  }

  /**
   * Trigger a milestone notification
   */
  private triggerMilestoneNotification(
    goal: Goal,
    milestone: number,
    currentProgress: number
  ): void {
    try {
      // Get goal display info
      const goalName = this.getGoalDisplayName(goal);
      const progressText = this.getProgressText(goal, currentProgress);

      // Trigger notification via global bridge
      if ((window as any).__showAchievement) {
        (window as any).__showAchievement({
          id: `goal-milestone-${goal.id}-${milestone}`,
          name: milestone === 100 ? 'Goal Completed!' : `${milestone}% Complete!`,
          description: `${goalName}: ${progressText}`,
          category: 'goal',
          tier: milestone === 100 ? 'gold' : milestone >= 75 ? 'silver' : 'bronze',
          points: milestone === 100 ? 100 : milestone,
          iconUrl: '', // Will use default icon
          unlockedAt: Date.now(),
          type: 'milestone'
        });
      }

      logger.info(`Goal milestone notification triggered: ${goal.id} - ${milestone}%`);
    } catch (error) {
      logger.error('Error triggering goal milestone notification:', error);
    }
  }

  /**
   * Get display name for a goal
   */
  private getGoalDisplayName(goal: Goal): string {
    const typeNames = {
      time: 'Training Time',
      consistency: 'Training Sessions',
      weight: 'Weight Target'
    };

    const frequencyNames = {
      weekly: 'Weekly',
      monthly: 'Monthly'
    };

    return `${frequencyNames[goal.frequency]} ${typeNames[goal.type]}`;
  }

  /**
   * Get progress text for a goal
   */
  private getProgressText(goal: Goal, currentProgress: number): string {
    return `${currentProgress} / ${goal.targetValue} ${goal.umShort}`;
  }

  /**
   * Check goals progress from training
   */
  checkGoalsProgressFromTraining(
    goals: Goal[],
    progressUpdates: Record<string, GoalProgress>,
    previousProgress: Record<string, GoalProgress>
  ): void {
    goals.forEach(goal => {
      if (!goal.id || !goal.isActive) return;

      const current = progressUpdates[goal.id];
      const previous = previousProgress[goal.id];

      if (current && current.currentValue !== undefined) {
        const prevProgress = previous?.currentValue || 0;
        this.checkGoalMilestone(goal, current.currentValue, prevProgress);
      }
    });
  }

  /**
   * Trigger goal creation notification
   */
  triggerGoalCreatedNotification(goal: Goal): void {
    try {
      const goalName = this.getGoalDisplayName(goal);

      if ((window as any).__showAchievement) {
        (window as any).__showAchievement({
          id: `goal-created-${goal.id}`,
          name: 'New Goal Created!',
          description: `${goalName} - Target: ${goal.targetValue} ${goal.umShort}`,
          category: 'goal',
          tier: 'bronze',
          points: 10,
          iconUrl: '', // Will use default icon
          unlockedAt: Date.now(),
          type: 'goal_created'
        });
      }
    } catch (error) {
      logger.error('Error triggering goal created notification:', error);
    }
  }
}

// Export singleton instance
const goalNotificationsService = new GoalNotificationsService();
export default goalNotificationsService;